import React, { useEffect, useRef, useState } from 'react';

const SUITS = [
  { char: '♠', color: 'rgba(255,255,255,0.52)' },
  { char: '♥', color: 'rgba(230,100,100,0.55)' },
  { char: '♦', color: 'rgba(230,100,100,0.55)' },
  { char: '♣', color: 'rgba(255,255,255,0.52)' },
];

// Fixed placements (restored)
// Slightly less dense than original, extended closer to top and bottom
const placements = [
  { top: '6%', left: '12%', size: 100, rot: -10, driftX: '25px', floatY: '32px', duration: '12s' },
  { top: '10%', left: '58%', size: 72, rot: 24, driftX: '-18px', floatY: '29px', duration: '10s' },
  { top: '14%', left: '70%', size: 78, rot: 10, driftX: '-25px', floatY: '32px', duration: '12s' },
  { top: '22%', left: '86%', size: 81, rot: 14, driftX: '-29px', floatY: '36px', duration: '13s' },
  { top: '34%', left: '28%', size: 73, rot: -18, driftX: '18px', floatY: '25px', duration: '10s' },
  { top: '48%', left: '48%', size: 130, rot: -6, driftX: '29px', floatY: '36px', duration: '14s' },
  { top: '46%', left: '92%', size: 70, rot: 8, driftX: '-25px', floatY: '32px', duration: '12s' },
  { top: '58%', left: '70%', size: 76, rot: -22, driftX: '22px', floatY: '32px', duration: '11s' },
  { top: '64%', left: '12%', size: 59, rot: -12, driftX: '18px', floatY: '29px', duration: '11s' },
  { top: '68%', left: '82%', size: 119, rot: 6, driftX: '-29px', floatY: '40px', duration: '15s' },
  { top: 'calc(100% - 28px)', left: '56%', size: 88, rot: 20, driftX: '22px', floatY: '28px', duration: '12s' },
  { top: 'calc(100% - 40px)', left: '90%', size: 72, rot: 8, driftX: '-16px', floatY: '18px', duration: '12s' },
  { top: 'calc(100% - 52px)', left: '14%', size: 66, rot: -6, driftX: '16px', floatY: '20px', duration: '12s' },
  { top: 'calc(100% - 32px)', left: '22%', size: 58, rot: -4, driftX: '14px', floatY: '18px', duration: '12s' },
];

// Global scale for suit icon sizes on hero background
const SIZE_SCALE = 1;

const percentToPx = (value, total) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.endsWith('%')) {
      const num = parseFloat(trimmed);
      if (!Number.isNaN(num)) return (num / 100) * total;
    }
    const calcMatch = trimmed.match(/^calc\(100%\s*-\s*(\d+(?:\.\d+)?)px\)$/);
    if (calcMatch) {
      const px = parseFloat(calcMatch[1]);
      return Math.max(total - px, 0);
    }
  }
  return parseFloat(value) || 0;
};

const Y_OFFSET_PX = 0; // use placements as-is to reach top/bottom

const BackgroundSuits = () => {
  const containerRef = useRef(null);
  const [positions, setPositions] = useState(null); // [{top,left}]
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dragStartRef = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    // Use fixed placements provided by user for consistency
    setPositions(
      placements.map((p) => ({
        top: percentToPx(p.top, height) + Y_OFFSET_PX,
        left: percentToPx(p.left, width),
        size: p.size,
        rot: p.rot,
        driftX: p.driftX,
        floatY: p.floatY,
        duration: p.duration,
      }))
    );
  }, []);

  // Pause background suit animations when hero is off-screen
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsActive(entry.isIntersecting);
      },
      { root: null, threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      if (draggingIndex === null) return;
      e.preventDefault();
      setPositions((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;
        next[draggingIndex] = {
          top: dragStartRef.current.top + dy,
          left: dragStartRef.current.left + dx,
        };
        return next;
      });
    };
    const handleUp = () => {
      setDraggingIndex(null);
      document.body.classList.remove('suits-dragging');
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingIndex]);

  const onMouseDown = (idx) => (e) => {
    if (e.button !== 0) return; // left click only
    e.preventDefault();
    e.stopPropagation();
    setDraggingIndex(idx);
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      top: rect.top - (containerRef.current?.getBoundingClientRect().top || 0),
      left: rect.left - (containerRef.current?.getBoundingClientRect().left || 0),
    };
    document.body.classList.add('suits-dragging');
  };

  return (
    <div
      ref={containerRef}
      className={`suits-field ${isActive ? 'suits-active' : 'suits-paused'}`}
      aria-hidden="true"
    >
      {positions?.map((p, idx) => {
        const suit = SUITS[idx % SUITS.length];
        return (
          <div
            key={idx}
            className={`suit-wrap${draggingIndex === idx ? ' dragging' : ''}`}
            onMouseDown={onMouseDown(idx)}
            style={{
              top: `${p.top}px`,
              left: `${p.left}px`,
              '--rot': `${p.rot}deg`,
              '--duration': p.duration,
              '--driftX': p.driftX,
              '--floatY': p.floatY,
              '--suit-size': `${Math.round(p.size * SIZE_SCALE)}px`,
              '--suit-color': suit.color,
            }}
          >
            <span className="suit" style={{ color: 'var(--suit-color)' }}>{suit.char}</span>
          </div>
        );
      })}
    </div>
  );
};

export default BackgroundSuits;


